/**
 * ScoreRibbon — Cricbuzz-style automation panel:
 *   ▸ Recent overs strip (last ~12 balls colored by outcome)
 *   ▸ Partnership chip (current pair runs/balls)
 *   ▸ Required-rate display when chasing
 *   ▸ Match-phase indicator (Powerplay / Middle / Death)
 *
 * All derived from props the page already has.
 */
import { useMemo } from "react";
import { Zap, Users, Target } from "lucide-react";
import { isHundred, ballsPerOver, formatBallReference } from "../../utils/hundredDisplay";

// RETIRED_HURT / RETIRED_NOT_OUT are not dismissals — the batter
// temporarily leaves the field but can return. The ribbon should NOT
// render them with the wicket colour or the "wk" label (operators were
// counting them as dismissals at a glance). RETIRED_OUT IS a wicket
// (batter walks off, doesn't return) so it keeps the wicket treatment.
const NON_WICKET_RETIRED = new Set(["RETIRED_HURT", "RETIRED_NOT_OUT"]);

function isWicketBall(b) {
  return b.has_wicket && !NON_WICKET_RETIRED.has(b.wicket || "");
}

function ballColor(b) {
  if (b.is_phantom) return "bg-bg-secondary/30 text-text-secondary border border-dashed border-text-secondary/40";
  if (isWicketBall(b)) return "bg-red-600 text-white";
  if (b.runs_off_bat === 6) return "bg-purple-600 text-white";
  if (b.runs_off_bat === 4) return "bg-emerald-500 text-white";
  if (b.extras > 0) return "bg-amber-500/30 text-amber-300";
  if (b.runs_off_bat > 0) return "bg-bg-secondary text-text-primary";
  return "bg-bg-secondary/50 text-text-secondary"; // dot
}

function ballLabel(b) {
  // Phantom balls (stop-gap for irregular umpiring) show a dash —
  // they exist for over-count math only, no real delivery.
  if (b.is_phantom) return "—";
  // Real wickets win regardless of runs (cricket convention). Retired-
  // hurt / retired-not-out fall through to the normal run/extras render
  // because they're not dismissals.
  if (isWicketBall(b)) return "wk";
  // Overthrow: show "<off-bat-run>+<overthrow>". Under the founder model
  // (2026-05-30) runs_off_bat already INCLUDES the overthrow portion, so
  // the physical off-bat = runs_off_bat - overthrow_runs. Display order
  // is "<off-bat>+<overthrow>" — e.g. 1 ran + 2 overthrow renders as
  // "1+2" (total still 3 in scorecard / partnership / team total).
  const ot = b.overthrow_runs || 0;
  if (ot > 0) {
    // Math.max guards old pre-2026-05-30 rows where runs_off_bat was just
    // the off-bat portion and overthrow was additive — that combination
    // would otherwise render a negative number here.
    const offBat = Math.max(0, (b.runs_off_bat || 0) - ot);
    return `${offBat}+${ot}`;
  }
  // Illegal deliveries (wd / nb) MUST be detected before the boundary
  // shortcut below — otherwise a 4 or 6 off a no-ball returns "4" /
  // "6" and the "nb" suffix never gets appended. Per operator
  // preference (2026-05-22) the no-ball label exposes off-bat runs
  // (0nb / 1nb / 4nb / 6nb) so reviewers can tell a no-ball-boundary
  // from a regular boundary at a glance.
  if (!b.is_legal) {
    const flags = b.flags || [];
    // 2026-06-06 — Penalty wins over wd / nb in the chip label too.
    // Mirrors the backend commentary precedence fix so the timeline
    // chip and the commentary line agree (both say "PN").
    if (flags.includes("pn")) return "PN";
    if (flags.includes("wd")) {
      const wr = b.wide_runs || 0;
      return wr > 1 ? `Wd${wr}` : "Wd";
    }
    if (flags.includes("nb")) {
      const offBat = b.runs_off_bat || 0;
      return `${offBat}nb`;
    }
    return "•";
  }
  // Legal-delivery penalty (rare — penalty without wide/no-ball but
  // ball is still legal). Render PN before the runs fallback.
  if ((b.flags || []).includes("pn")) return "PN";
  if (b.runs_off_bat === 6) return "6";
  if (b.runs_off_bat === 4) return "4";
  // Legal deliveries can ALSO carry byes / leg-byes (batter didn't hit
  // the ball but they ran on the wicket-keeper's miss / off the pad).
  // Previously these fell through to runs_off_bat which is 0 for byes
  // → the ribbon rendered "0" (dot) and operators couldn't tell a
  // genuine dot apart from a 1-bye scampered single. Now: show "1B" /
  // "2B" for byes and "1LB" / "2LB" for leg-byes — same shape as the
  // wide / no-ball labels above.
  const flags = b.flags || [];
  if (flags.includes("b")) {
    const byes = b.bye_runs || b.extras || 1;
    return `${byes}B`;
  }
  if (flags.includes("lb")) {
    const lbyes = b.leg_bye_runs || b.extras || 1;
    return `${lbyes}LB`;
  }
  return String(b.runs_off_bat || 0);
}

export default function ScoreRibbon({
  recentBalls = [],          // [{ runs_off_bat, extras, has_wicket, is_legal, ... }]
  strikerStat = null,        // { runs, balls }
  nonStrikerStat = null,
  aggregate = null,          // { runs, wickets, completed_overs, legal_balls, target_runs, max_overs, is_chasing }
  matchType = null,          // { balls_per_over, max_overs }
  currentPartnership = null, // { runs, balls } from /api/matches/.../partnerships/ — preferred when present
}) {
  // Server-computed partnership (includes balls + per-batter splits) is
  // the ONLY source of truth. The old striker.runs + non_striker.runs
  // fallback leaked prior-partnership runs into the chip whenever the
  // non-striker was already batting in the previous partnership.
  // Removed 2026-07-08.
  //
  // When the server hasn't returned yet OR a new pair just walked in
  // (no ball faced yet), we default to 0/0 so the chip stays visible
  // through the transition. BallByBallPage clears currentPartnership
  // to null on wicket / innings-change so the display flips to 0/0
  // immediately, not after the refetch lands.
  const pair = useMemo(() => {
    if (currentPartnership && currentPartnership.runs != null) {
      return {
        runs: currentPartnership.runs ?? 0,
        balls: currentPartnership.balls ?? 0,
      };
    }
    return { runs: 0, balls: 0 };
  }, [currentPartnership]);

  // Tests have no per-innings overs cap, so "from X balls" + RRR are
  // meaningless. Detector: explicit is_test flag (when present) or the
  // absence of a max_overs on the format. For Tests we surface
  // "Need N to win" plus a separate "Behind by N" / "Lead by N" chip
  // below — no balls-left, no rate.
  // 2026-06-05 (v3) — code === "TEST" is the deterministic signal.
  // matchType.is_test isn't always populated by the backend serializer
  // and matchType.max_overs is null for Tests but ALSO undefined when
  // the payload omits the field — so the previous detector misfired in
  // both directions. Code is the contract: the backend MatchType row
  // for Tests has code="TEST" and the SPA always receives this field.
  const isTest = (matchType?.code || "").toUpperCase() === "TEST"
    || !!matchType?.is_test
    || matchType?.max_overs === null
    || matchType?.max_overs === 0;

  // Required rate — uses the DLS-revised target / max_overs when set.
  // aggregate.max_overs is the per-innings override (e.g. 20 overs after
  // DLS in a rain-affected match); falling through to matchType.max_overs
  // would show "from 300 balls" even after DLS in a T20.
  // DLS targets are the exact "score to reach", so need = target − runs.
  // Auto-set targets follow cricket's "score to beat" rule, so +1 to win.
  // Hundred RRR is omitted — the "Need N from B balls" line already
  // conveys the tempo in a 100-ball innings and a "runs per over" figure
  // has no cricketing meaning there.
  const required = useMemo(() => {
    if (isTest) return null;          // Tests use the Test-specific chips below.
    if (!aggregate?.is_chasing) return null;
    const isDLS = aggregate.revised_target_runs != null;
    const effective_target = isDLS ? aggregate.revised_target_runs : aggregate.target_runs;
    if (effective_target == null) return null;
    const max_overs = aggregate.max_overs || matchType?.max_overs || 50;
    const bpo = ballsPerOver(matchType);
    const balls_left = max_overs * bpo - aggregate.legal_balls;
    const need = (isDLS ? effective_target : effective_target + 1) - aggregate.runs;
    if (balls_left <= 0 || need <= 0) return null;
    if (isHundred(matchType)) return { need, balls_left, rrr: null };
    const rrr = (need * bpo) / balls_left;
    return { need, balls_left, rrr };
  }, [aggregate, matchType, isTest]);

  // Test 4th-innings chase: just "Need N to win" + "Behind/Lead by M".
  // target_runs in inn4 is the run gap the chasing side must surpass
  // (= opponent total − own first-innings total), set by
  // compute_fourth_innings_target. need = (target+1) − current,
  // behind = target − current (positive if still chasing; flips to
  // "lead" once the chasing side has overtaken).
  const testChase = useMemo(() => {
    if (!isTest) return null;
    if (!aggregate?.is_chasing) return null;
    const target = aggregate.revised_target_runs ?? aggregate.target_runs;
    if (target == null) return null;
    const runs = aggregate.runs ?? 0;
    const need = Math.max(0, target + 1 - runs);
    const diff = runs - target;                  // negative ⇒ behind, positive ⇒ lead
    return { need, diff };
  }, [aggregate, isTest]);

  // Match phase. The Hundred has its own ball-aligned thresholds (PP =
  // first 25 balls, then middle 26-75, death 76-100) — surfaced as a
  // separate branch off the format code so the over-based ranges below
  // don't misfire on a 20-set Hundred innings.
  const phase = useMemo(() => {
    if (!aggregate || !matchType?.max_overs) return null;
    const code = (matchType.code || "").toUpperCase();
    if (code === "HUNDRED") {
      // ECB phase spec — Powerplay: balls 1-25, Middle: 26-75, Death: 76-100.
      // Chip reflects the phase currently in play. Ball 25 completes PP
      // (lb goes 24→25) and the chip flips to Middle. Ball 75 completes
      // Middle (lb 74→75) and chip flips to Death. Previously Middle
      // ran to `lb < 76`, leaving Middle showing during ball 76 which
      // is the first Death ball.
      //
      // 2026-07-10 — rain-affected custom windows: when the operator
      // has set Powerplay / Middle / Death ranges via
      // PowerplayOverrideField, aggregate.match_type.powerplay_windows
      // arrives on matchType.powerplay_windows. Pick the window whose
      // [start_ball, end_ball] brackets the ball about to be bowled.
      const lb = aggregate.legal_balls ?? 0;
      const custom = Array.isArray(matchType?.powerplay_windows)
        ? matchType.powerplay_windows.filter(
            (w) => w && w.type && w.start_ball != null && w.end_ball != null,
          )
        : [];
      if (custom.length > 0) {
        const pos = Math.max(1, lb + 1);
        const hit = custom.find((w) => pos >= w.start_ball && pos <= w.end_ball);
        if (hit) {
          if (hit.type === "POWERPLAY") return { name: "Powerplay", tone: "text-emerald-400" };
          if (hit.type === "MIDDLE")    return { name: "Middle",    tone: "text-text-secondary" };
          if (hit.type === "DEATH")     return { name: "Death",     tone: "text-red-400" };
        }
      }
      if (lb < 25) return { name: "Powerplay", tone: "text-emerald-400" };
      if (lb < 75) return { name: "Middle",    tone: "text-text-secondary" };
      return { name: "Death", tone: "text-red-400" };
    }
    const ov = aggregate.completed_overs ?? 0;
    if (matchType.max_overs <= 20) {
      if (ov < 6)  return { name: "Powerplay", tone: "text-emerald-400" };
      if (ov < 15) return { name: "Middle",    tone: "text-text-secondary" };
      return { name: "Death", tone: "text-red-400" };
    }
    if (matchType.max_overs <= 50) {
      if (ov < 10) return { name: "PP1", tone: "text-emerald-400" };
      if (ov < 40) return { name: "PP2", tone: "text-text-secondary" };
      return { name: "PP3", tone: "text-amber-400" };
    }
    return null; // Tests — no phase
  }, [aggregate, matchType]);

  return (
    // 2026-05-29 — single-line layout (founder ask). Was flex-wrap +
    // gap-3 + 12 balls; on the standard left-pane width the phase
    // chip ("Powerplay / Middle / Death") wrapped to the next line.
    // Now: flex-nowrap + overflow-x-auto + gap-2 + 8-ball strip so
    // everything (Recent · Partnership · Powerplay) stays on one
    // line and scrolls horizontally if it ever does overflow.
    <div className="flex items-center gap-2 text-[11px] flex-nowrap overflow-x-auto">
      {recentBalls.length > 0 && (
        <div className="flex items-center gap-1 min-w-0 shrink-0">
          <span className="text-[9px] uppercase tracking-wider text-text-secondary mr-1 shrink-0">Recent</span>
          <div className="flex gap-0.5">
            {recentBalls.slice(-8).map((b, i) => (
              <span
                key={i}
                title={formatBallReference(matchType, {
                  overNumber: b.over_number,
                  ballInOver: b.ball_in_over,
                })}
                className={`min-w-[18px] h-5 rounded-sm flex items-center justify-center font-mono text-[10px] shrink-0 ${ballColor(b)}`}
              >
                {ballLabel(b)}
              </span>
            ))}
          </div>
        </div>
      )}
      {aggregate?.innings_number && (
        <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-bg-secondary/40 border border-border-default shrink-0">
          <Users className="w-3 h-3 text-text-secondary" />
          <span className="font-mono">{pair.runs}/{pair.balls}</span>
          <span className="text-text-secondary text-[10px]">P'ship</span>
        </span>
      )}
      {required && (
        <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/30 text-amber-300 shrink-0">
          <Target className="w-3 h-3" />
          <span className="font-mono">Need {required.need} from {required.balls_left}{isHundred(matchType) ? " balls" : ""}</span>
          {required.rrr != null && (
            <span className="text-[10px] opacity-80">RR {required.rrr.toFixed(2)}</span>
          )}
        </span>
      )}
      {/* Test 4th innings — runs-to-win + lead/behind only. No balls
          quota, no required rate (Test cricket has no per-innings
          overs cap, so RRR would be meaningless). */}
      {testChase && (
        <>
          {testChase.need > 0 && (
            <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/30 text-amber-300 shrink-0">
              <Target className="w-3 h-3" />
              <span className="font-mono">Need {testChase.need} to win</span>
            </span>
          )}
          {testChase.diff !== 0 && (
            <span
              className={`flex items-center gap-1 px-1.5 py-0.5 rounded border shrink-0 font-mono ${
                testChase.diff > 0
                  ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-300"
                  : "bg-rose-500/15 border-rose-500/30 text-rose-300"
              }`}
              title={testChase.diff > 0
                ? "Chasing side has overtaken the target"
                : "Runs the chasing side still trails by"}
            >
              {testChase.diff > 0
                ? `Lead by ${testChase.diff}`
                : `Behind by ${-testChase.diff}`}
            </span>
          )}
        </>
      )}
      {phase && (
        <span className={`flex items-center gap-1 px-1.5 py-0.5 rounded bg-bg-secondary/40 border border-border-default shrink-0 ${phase.tone}`}>
          <Zap className="w-3 h-3" />
          <span className="text-[10px] uppercase tracking-wider">{phase.name}</span>
        </span>
      )}
    </div>
  );
}
